import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { numberToWord } from 'app/shared/utils/string';
import { UseSubmitAction as useSubmitAction } from 'app/shared/utils/useSubmitAction';
import { SurveyQuestion } from 'app/shared/typings/surveyQuestions';
import { CreateSurveyAnswers } from 'app/shared/graphql/surveyQuestions/mutationHooks';
import { GetSurveyQuestions } from 'app/shared/graphql/surveyQuestions/queryHooks';
import { AuthContext } from 'app/shared/context/Auth';
import GenericForm from 'app/shared/components/atoms/GenericForm';
import { GenericSmallModal } from 'app/shared/components/atoms/GenericSmallModal';
import { H3, H4 } from 'app/shared/components/atoms/TypographyManualCSS';

import OrganizerSurveyForm from './OrganizerSurveyForm';
import OrganizerSurveyFormSchema from './OrganizerSurveyFormSchema';

const ModalTitle = styled(H3)`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.h3.lg};
    font-weight: 400;
    margin-bottom: 5px;
  `}
`;

const SuccessMessage = styled(H4)`
  ${({ theme }) => css`
    font-weight: 600;
    color: ${theme.colors.green600};
    margin-top: 25px;
  `}
`;

const getSurveyAnswersInitialValue = (surveyQuestions: SurveyQuestion[]) => {
  return surveyQuestions.reduce(
    (o, surveyQuestion) => ({
      ...o,
      [surveyQuestion.id?.toString() || '']: {
        answerText: null,
        optionId: null,
      },
    }),
    {}
  );
};

const getSurveyAnswersSubmitVariable = (surveyAnswers: {
  [id: string]: string | number | null;
}) => {
  return Object.keys(surveyAnswers).map((surveyQuestionId: string) => {
    return {
      surveyQuestionId: Number(surveyQuestionId),
      // @ts-ignore
      answerText: surveyAnswers[surveyQuestionId].answerText || null,
      surveyQuestionOptionId:
        // @ts-ignore
        surveyAnswers[surveyQuestionId].optionId || null,
    };
  });
};

const successMsgLingeringTimeNumSeconds = 3;

export const OrganizerSurveyModal = () => {
  const intl = useIntl();
  const { reloadSession } = useContext(AuthContext);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    loading: loadingSurveyQuestions,
    error: errorSurveyQuestions,
    data: dataSurveyQuestions,
  } = GetSurveyQuestions({
    surveyType: 'organizer',
  });

  const createSurveyAnswersAction = CreateSurveyAnswers();

  const handleSubmit = useSubmitAction({
    submitAction: createSurveyAnswersAction,
    submitVariables: (values: any) => ({
      surveyAnswers: getSurveyAnswersSubmitVariable(values.surveyAnswers),
    }),
    failureMsg: intl.formatMessage({
      id: 'admin.organizerSurvey.failureMessage',
    }),
    onSuccess: () => {
      setIsSuccess(true);
      // After X seconds, reload session, which makes frontend contain latest shouldPromptForOrganizerSurvey value,
      // which will be false, so entire OrganizerSurvey modal (this component) will be unmounted
      setTimeout(() => {
        reloadSession();
      }, successMsgLingeringTimeNumSeconds * 1000);
    },
  });

  if (
    errorSurveyQuestions ||
    loadingSurveyQuestions ||
    !dataSurveyQuestions ||
    dataSurveyQuestions.surveyQuestions.surveyQuestions.length == 0
  ) {
    return null;
  }

  const surveyQuestions = [
    ...dataSurveyQuestions.surveyQuestions.surveyQuestions,
  ].sort((a: SurveyQuestion, b: SurveyQuestion) =>
    (a.position || 0) > (b.position || 0) ? 1 : -1
  );

  const titleNumberWord = numberToWord(surveyQuestions.length);

  const formInitialValues = {
    surveyAnswers: getSurveyAnswersInitialValue(surveyQuestions),
  };

  return (
    <GenericSmallModal>
      <ModalTitle>
        {intl.formatMessage(
          {
            id: 'admin.organizerSurvey.xQuickQuestions',
          },
          {
            numQuestions: titleNumberWord,
          }
        )}
      </ModalTitle>
      {isSuccess ? (
        <SuccessMessage>
          {intl.formatMessage({
            id: 'admin.organizerSurvey.successMessage',
          })}
        </SuccessMessage>
      ) : (
        <GenericForm
          formInitialValues={formInitialValues}
          renderFormComponent={(renderProps: any) => (
            <OrganizerSurveyForm
              formikProps={renderProps.formikProps}
              surveyQuestions={surveyQuestions}
            />
          )}
          onSubmit={handleSubmit}
          formSchema={OrganizerSurveyFormSchema(intl, surveyQuestions)}
          dataQaId="organizer-survey"
        />
      )}
    </GenericSmallModal>
  );
};
